/**
 * Chat Mode Redux Slice (Issue #302)
 *
 * Manages chat mode state for the mode switching system.
 * Supports: help, settings, ask, plan, drbfm modes.
 */
import { createAsyncThunk, createSelector, createSlice, PayloadAction } from '@reduxjs/toolkit';
import { fetchVerificationStatus } from '@features/featureFlags/featureFlagsSlice';
import { RootState } from '@store/index';

import {
  ChatMode,
  SwitchModeRequest,
  SwitchModeResponse,
} from '../../types/chat';
import chatService from '../../services/chatService';

/** The one mode gated by the per-tenant design-verification flag (#1214). */
const VERIFICATION_MODE_ID = 'verification';

// ============================================================
// State Interface
// ============================================================

interface ChatModeState {
  // Available modes
  modes: ChatMode[];

  // Current mode
  currentMode: string;
  currentModeData: Record<string, any>;
  previousMode: string | null;

  // UI state
  showModeSelector: boolean;
  showSlashSuggestions: boolean;
  slashSuggestionQuery: string;

  // Mode switching state
  modeSwitching: boolean;
  modeSwitchError: string | null;
}

// ============================================================
// Initial State
// ============================================================

const initialState: ChatModeState = {
  modes: [
    {
      id: 'help',
      name: 'ヘルプ',
      description: 'システムの使い方とコマンド一覧',
      color: '#4caf50',
      icon: 'help',
      isActive: true,
      isCustom: false,
      sortOrder: 10,
      settings: { temperature: 0.3, max_tokens: 1000 },
    },
    {
      id: 'settings',
      name: '設定',
      description: 'データソース状態・利用統計',
      color: '#ff9800',
      icon: 'settings',
      isActive: true,
      isCustom: false,
      sortOrder: 20,
      settings: { temperature: 0.2, max_tokens: 1500 },
    },
    {
      id: 'ask',
      name: 'Q&A',
      description: '質問応答モード（デフォルト）',
      color: '#1976d2',
      icon: 'question_answer',
      isActive: true,
      isCustom: false,
      sortOrder: 30,
      settings: { temperature: 0.7, max_tokens: 1500 },
    },
    {
      id: 'plan',
      name: '設計深掘り',
      description: '設計に関する深い議論',
      color: '#9c27b0',
      icon: 'architecture',
      isActive: true,
      isCustom: false,
      sortOrder: 40,
      settings: { temperature: 0.5, max_tokens: 2000 },
    },
    {
      id: 'drbfm',
      name: 'DRBFM分析',
      description: 'DRBFM分析サポート',
      color: '#f44336',
      icon: 'assessment',
      isActive: true,
      isCustom: false,
      sortOrder: 50,
      settings: { temperature: 0.4, max_tokens: 2000 },
    },
    {
      id: 'verification',
      name: '設計検証',
      description: '設計検証項目の分析・改善支援',
      color: '#00897b',
      icon: 'verified',
      isActive: true,
      isCustom: false,
      sortOrder: 55,
      settings: { temperature: 0.4, max_tokens: 2500 },
    },
    {
      id: 'cad',
      name: 'CAD分析',
      description: 'CADデータの構造・設計レビュー',
      color: '#607d8b',
      icon: 'view_in_ar',
      isActive: true,
      isCustom: false,
      sortOrder: 60,
      settings: { temperature: 0.5, max_tokens: 2000 },
    },
  ],

  currentMode: localStorage.getItem('aiona_default_chat_mode') || 'ask',
  currentModeData: {},
  previousMode: null,

  showModeSelector: false,
  showSlashSuggestions: false,
  slashSuggestionQuery: '',

  modeSwitching: false,
  modeSwitchError: null,
};

// ============================================================
// Async Thunks
// ============================================================

/**
 * Switch mode for a thread
 */
export const switchMode = createAsyncThunk<
  SwitchModeResponse,
  { threadId: string; request: SwitchModeRequest }
>(
  'chatMode/switchMode',
  async ({ threadId, request }, { rejectWithValue }) => {
    try {
      const response = await chatService.switchMode(threadId, request);
      return response;
    } catch (error) {
      return rejectWithValue(
        error instanceof Error ? error.message : 'Failed to switch mode'
      );
    }
  }
);

// ============================================================
// Slice
// ============================================================

const chatModeSlice = createSlice({
  name: 'chatMode',
  initialState,
  reducers: {
    // Mode selection UI
    setShowModeSelector: (state, action: PayloadAction<boolean>) => {
      state.showModeSelector = action.payload;
    },

    // Slash command suggestions
    setShowSlashSuggestions: (state, action: PayloadAction<boolean>) => {
      state.showSlashSuggestions = action.payload;
    },

    // Direct mode switching (without API call)
    setCurrentMode: (state, action: PayloadAction<string>) => {
      state.previousMode = state.currentMode;
      state.currentMode = action.payload;
      // Clear mode-specific data when switching modes (Bugbot fix)
      state.currentModeData = {};
      // Persist to localStorage so reload restores last mode (#494 B-3)
      localStorage.setItem('aiona_default_chat_mode', action.payload);
    },

    setModeData: (state, action: PayloadAction<Record<string, any>>) => {
      state.currentModeData = action.payload;
    },

    // Reset mode to default
    resetMode: (state) => {
      state.previousMode = state.currentMode;
      state.currentMode = 'ask';
      state.currentModeData = {};
      state.showModeSelector = false;
      state.showSlashSuggestions = false;
      state.slashSuggestionQuery = '';
    },
  },
  extraReducers: (builder) => {
    // switchMode
    builder
      .addCase(switchMode.pending, (state) => {
        state.modeSwitching = true;
        state.modeSwitchError = null;
      })
      .addCase(switchMode.fulfilled, (state, action) => {
        state.modeSwitching = false;
        state.previousMode = action.payload.previous_mode || state.currentMode;
        state.currentMode = action.payload.mode.id;
        state.showModeSelector = false;
        // Clear mode-specific data when switching modes (Bugbot fix)
        state.currentModeData = {};
        // Persist to localStorage (#494 B-3)
        localStorage.setItem('aiona_default_chat_mode', action.payload.mode.id);
      })
      .addCase(switchMode.rejected, (state, action) => {
        state.modeSwitching = false;
        state.modeSwitchError = action.payload as string;
      })
      // currentMode is rehydrated from localStorage, so a tenant that loses the
      // feature can arrive pinned to it — leaving the chip unrenderable and the
      // user unable to switch away.
      .addCase(fetchVerificationStatus.fulfilled, (state, action) => {
        if (action.payload === false && state.currentMode === VERIFICATION_MODE_ID) {
          state.currentMode = 'ask';
          state.currentModeData = {};
          localStorage.setItem('aiona_default_chat_mode', 'ask');
        }
      });
  },
});

// ============================================================
// Actions
// ============================================================

export const {
  setShowModeSelector,
  setShowSlashSuggestions,
  setCurrentMode,
  setModeData,
  resetMode,
} = chatModeSlice.actions;

// ============================================================
// Selectors
// ============================================================

// Memoised: .filter() returns a new array every call, so an unmemoised version
// re-renders every subscriber on any state change. React-Redux warns about it.
//
// The gate lives here because the mode picker and the slash popup both read this
// selector; filtering only one would leave the other exposed.
export const selectActiveModes = createSelector(
  [
    (state: RootState) => state.chatMode.modes,
    (state: RootState) => state.featureFlags?.designVerificationEnabled,
  ],
  (modes, designVerificationEnabled) =>
    modes.filter(
      (m) =>
        m.isActive &&
        (m.id !== VERIFICATION_MODE_ID || designVerificationEnabled !== false),
    ),
);

// Current mode selectors
export const selectCurrentMode = (state: RootState) =>
  state.chatMode.currentMode;
export const selectCurrentModeData = (state: RootState) =>
  state.chatMode.currentModeData;
export const selectCurrentModeConfig = (state: RootState) =>
  state.chatMode.modes.find((m) => m.id === state.chatMode.currentMode);

// UI state selectors
export const selectShowModeSelector = (state: RootState) =>
  state.chatMode.showModeSelector;

// Mode switching selectors
export const selectModeSwitching = (state: RootState) =>
  state.chatMode.modeSwitching;

// ============================================================
// Export
// ============================================================

export default chatModeSlice.reducer;
